import { useState } from 'react';
import { Globe, FileCode, Tag } from 'lucide-react';

const METHODS = [
  {
    id: 'dns',
    icon: Globe,
    title: 'DNS Record',
    tagline: 'Add a TXT record at your domain registrar',
    meta: 'Most secure · 5–30 min · Persistent',
    best: 'Best if: you manage your own DNS',
    recommended: true,
  },
  {
    id: 'html_file',
    icon: FileCode,
    title: 'HTML File',
    tagline: 'Upload a small file to your website',
    meta: 'Fast · Under 5 min · Persistent',
    best: 'Best if: you have FTP or hosting file access',
  },
  {
    id: 'meta_tag',
    icon: Tag,
    title: 'Meta Tag',
    tagline: 'Add a single line to your homepage',
    meta: 'Easiest · Under 5 min · Requires CMS access',
    best: 'Best if: you use WordPress, Webflow, Wix, Squarespace',
  },
];

export default function VerifyMethodPicker({ domain, onContinue, starting }) {
  const [selected, setSelected] = useState(null);

  return (
    <div>
      <h1 className="font-display" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--white)' }}>
        Verify your domain — {domain}
      </h1>
      <p className="font-body" style={{ color: 'var(--ts)', fontSize: '1rem', marginTop: '0.5rem',
            lineHeight: 1.5 }}>
        We need to confirm you own this domain before we can start monitoring. This takes about 5 minutes.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginTop: '1.75rem' }}>
        {METHODS.map((m) => {
          const Icon = m.icon;
          const active = selected === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setSelected(m.id)}
              data-testid={`verify-method-${m.id}`}
              className="text-left relative"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '1rem',
                padding: '1.1rem 1.25rem', borderRadius: 12, cursor: 'pointer',
                background: 'rgba(10,10,18,0.88)',
                border: `1px solid ${active ? 'var(--a)' : 'var(--b1)'}`,
                boxShadow: active ? '0 0 0 1px var(--a)' : 'none',
              }}
            >
              {/* radio */}
              <span style={{
                width: 18, height: 18, borderRadius: 999, marginTop: 3, flexShrink: 0,
                border: `2px solid ${active ? 'var(--a)' : 'var(--b2)'}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {active && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--a)' }} />}
              </span>

              <Icon size={20} style={{ color: 'var(--a)', flexShrink: 0, marginTop: 2 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="font-display" style={{ color: 'var(--white)', fontWeight: 600,
                      fontSize: '1rem' }}>
                  {m.title}
                </div>
                <div className="font-body" style={{ color: 'var(--tm)', fontSize: '0.86rem',
                      marginTop: 2 }}>
                  {m.tagline}
                </div>
                <div className="font-body" style={{ color: 'var(--ts)', fontSize: '0.78rem',
                      marginTop: 6 }}>
                  {m.meta}
                </div>
                <div className="font-body" style={{ color: 'var(--ts)', fontSize: '0.78rem',
                      marginTop: 2, fontStyle: 'italic' }}>
                  {m.best}
                </div>
              </div>

              {m.recommended && (
                <span className="font-mono" style={{
                  position: 'absolute', top: 12, right: 12, fontSize: '0.6rem', fontWeight: 700,
                  letterSpacing: '0.08em', padding: '0.15rem 0.45rem', borderRadius: 4,
                  background: 'var(--a)', color: '#fff',
                }}>
                  RECOMMENDED
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!selected || starting}
        onClick={() => selected && onContinue(selected)}
        data-testid="verify-method-continue"
        className="font-body font-medium inline-flex items-center gap-2"
        style={{
          marginTop: '1.5rem', padding: '0.7rem 1.4rem', borderRadius: 6, border: 'none',
          background: selected ? 'var(--a)' : 'var(--b1)',
          color: selected ? '#fff' : 'var(--ts)',
          cursor: selected && !starting ? 'pointer' : 'not-allowed',
          opacity: starting ? 0.7 : 1,
        }}
      >
        {starting ? 'Setting up…' : 'Continue →'}
      </button>

      <p className="font-body" style={{ color: 'var(--ts)', fontSize: '0.8rem', marginTop: '1.5rem' }}>
        Need help? →{' '}
        <a href="mailto:connect@acodia.co.uk" style={{ color: 'var(--a)' }}>connect@acodia.co.uk</a>
      </p>
    </div>
  );
}
